/* Dodona Moments: UGC gallery grouped by creator, with a shared-element viewer */
(() => {
  if (customElements.get('dodona-moments')) return;

  const EASE = 'cubic-bezier(0.22, 0.9, 0.24, 1)';
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const HASH_PREFIX = 'moment-';

  const isLoaded = (img) => Boolean(img && img.complete && img.naturalWidth);

  const imageReady = (img, timeout) => {
    if (!img || isLoaded(img)) return Promise.resolve();
    return new Promise((resolve) => {
      const done = () => {
        clearTimeout(timer);
        resolve();
      };
      const timer = setTimeout(done, timeout);
      img.addEventListener('load', done, { once: true });
      img.addEventListener('error', done, { once: true });
    });
  };

  // A fixed-position copy of an image, laid out at `rect`, used to morph between card and viewer.
  const createGhost = (img, rect) => {
    const ghost = document.createElement('div');
    ghost.className = 'dm-ghost';
    ghost.setAttribute('aria-hidden', 'true');
    ghost.style.left = `${rect.left}px`;
    ghost.style.top = `${rect.top}px`;
    ghost.style.width = `${rect.width}px`;
    ghost.style.height = `${rect.height}px`;
    const copy = document.createElement('img');
    copy.alt = '';
    copy.src = img.currentSrc || img.src;
    ghost.appendChild(copy);
    return ghost;
  };

  const invert = (from, to) =>
    `translate(${from.left - to.left}px, ${from.top - to.top}px) scale(${from.width / to.width}, ${from.height / to.height})`;

  class DodonaMoments extends HTMLElement {
    connectedCallback() {
      this.items = Array.from(this.querySelectorAll('[data-moment]'));
      this.dialog = this.querySelector('.dm-viewer');
      if (!this.dialog) return;

      const find = (selector) => this.dialog.querySelector(selector);
      this.ui = {
        bg: find('[data-viewer-bg]'),
        inner: find('[data-viewer-inner]'),
        track: find('[data-viewer-track]'),
        bars: find('[data-viewer-bars]'),
        meta: find('.dm-viewer__meta'),
        creator: find('[data-viewer-creator]'),
        caption: find('[data-viewer-caption]'),
        shop: find('[data-viewer-shop]'),
        prev: find('[data-viewer-prev]'),
        next: find('[data-viewer-next]'),
        nextMoment: find('[data-viewer-next-moment]'),
        status: find('[data-viewer-status]'),
      };
      this.ui.chrome = [find('.dm-viewer__top'), find('.dm-viewer__bottom'), this.ui.prev, this.ui.next];

      this.moment = -1;
      this.index = 0;
      this.slides = [];
      this.scrollLock = 0;

      this.items.forEach((item, i) => {
        const card = item.querySelector('[data-moment-open]');
        if (!card) return;
        card.addEventListener('click', () => this.open(i));
        card.addEventListener('pointerenter', () => this.preload(item), { once: true });
        card.addEventListener('focus', () => this.preload(item), { once: true });
      });

      this.ui.prev.addEventListener('click', () => this.goTo(this.index - 1));
      this.ui.next.addEventListener('click', () => this.goTo(this.index + 1));
      this.ui.nextMoment.addEventListener('click', () => this.switchMoment((this.moment + 1) % this.items.length));
      find('[data-viewer-close]').addEventListener('click', () => this.close());

      this.ui.track.addEventListener('scroll', () => this.onScroll(), { passive: true });
      this.ui.track.addEventListener('scrollend', () => {
        this.scrollLock = 0;
        this.onScroll();
      });

      this.dialog.addEventListener('cancel', (event) => {
        event.preventDefault();
        this.close();
      });
      this.dialog.addEventListener('close', () => {
        this.updateHash('');
        this.cleanup();
      });
      this.dialog.addEventListener('click', (event) => this.onDialogClick(event));
      this.onKeydown = this.onKeydown.bind(this);

      this.resizeObserver = new ResizeObserver(() => {
        if (this.dialog.open) this.goTo(this.index, false);
      });
      this.resizeObserver.observe(this.ui.track);

      this.setupReveal();

      this.hashSyncEnabled = !(window.Shopify && Shopify.designMode);
      if (this.hashSyncEnabled) {
        this.onHashChange = () => this.syncFromHash();
        window.addEventListener('hashchange', this.onHashChange);
        this.syncFromHash();
      }
    }

    disconnectedCallback() {
      this.revealObserver?.disconnect();
      this.resizeObserver?.disconnect();
      if (this.onHashChange) window.removeEventListener('hashchange', this.onHashChange);
      if (this.dialog?.open) this.dialog.close();
      this.cleanup();
    }

    /* Shareable links: #moment-<slug> opens a moment; closing clears it. */

    updateHash(slug) {
      if (!this.hashSyncEnabled) return;
      const url = new URL(location.href);
      url.hash = slug ? `${HASH_PREFIX}${slug}` : '';
      history.replaceState(history.state, '', url);
    }

    syncFromHash() {
      const raw = decodeURIComponent(location.hash.slice(1));
      if (raw.startsWith(HASH_PREFIX)) {
        this.openBySlug(raw.slice(HASH_PREFIX.length));
      } else if (!raw && this.dialog.open) {
        this.close();
      }
    }

    openBySlug(slug) {
      const i = this.items.findIndex((item) => item.dataset.slug === slug);
      if (i === -1) return;
      if (this.dialog.open) {
        if (this.moment !== i) this.switchMoment(i);
        return;
      }
      this.items[i].scrollIntoView({ block: 'center', behavior: 'instant' });
      this.open(i);
    }

    /* Grid */

    setupReveal() {
      if (!this.classList.contains('dm--anim')) return;

      this.revealObserver = new IntersectionObserver(
        (entries) => {
          let order = 0;
          entries.forEach((entry) => {
            if (!entry.isIntersecting) return;
            entry.target.style.setProperty('--dm-delay', `${order * 120}ms`);
            entry.target.classList.add('is-in');
            this.revealObserver.unobserve(entry.target);
            order += 1;
          });
        },
        { rootMargin: '0px 0px -8% 0px', threshold: 0.12 }
      );

      this.querySelectorAll('.dm-reveal, .dm-item').forEach((el) => this.revealObserver.observe(el));
    }

    // Warm the cache for a moment's photos before it is opened.
    preload(item) {
      const template = item.querySelector('template[data-moment-slides]');
      if (!template) return;
      template.content.querySelectorAll('img').forEach((source) => {
        const img = new Image();
        img.sizes = source.sizes;
        img.srcset = source.srcset;
        img.src = source.src;
      });
    }

    /* Viewer */

    open(i) {
      const item = this.items[i];
      if (!item || this.dialog.open) return;

      this.populate(i);
      this.lockScroll();
      this.dialog.showModal();
      document.addEventListener('keydown', this.onKeydown);
      this.goTo(0, false);
      this.ui.track.focus({ preventScroll: true });
      this.animateIn(item);
      this.updateHash(item.dataset.slug);
    }

    populate(i) {
      const item = this.items[i];
      const { track, bars, prev, next, creator, caption, shop, nextMoment } = this.ui;
      this.moment = i;

      const fragment = item.querySelector('template[data-moment-slides]').content.cloneNode(true);
      this.slides = Array.from(fragment.querySelectorAll('[data-slide]'));
      this.slides.forEach((slide, n) => {
        slide.classList.toggle('is-active', n === 0);
        const img = slide.querySelector('img');
        if (img) img.loading = 'eager';
        slide.addEventListener('click', () => {
          if (n !== this.index) this.goTo(n);
        });
      });
      track.replaceChildren(fragment);

      const count = this.slides.length;
      bars.hidden = count < 2;
      bars.replaceChildren(
        ...this.slides.map((_, n) => {
          const bar = document.createElement('button');
          bar.type = 'button';
          bar.className = 'dm-viewer__bar';
          bar.setAttribute('aria-label', `Photo ${n + 1} of ${count}`);
          bar.addEventListener('click', () => this.goTo(n));
          return bar;
        })
      );
      prev.hidden = count < 2;
      next.hidden = count < 2;

      const data = item.dataset;
      creator.textContent = data.creator || '';
      creator.hidden = !data.creator;
      if (data.creator && data.creatorUrl) {
        creator.href = data.creatorUrl;
      } else {
        creator.removeAttribute('href');
      }
      caption.textContent = data.caption || '';
      caption.hidden = !data.caption;

      const shopTemplate = item.querySelector('template[data-moment-shop]');
      shop.replaceChildren(...(shopTemplate ? [shopTemplate.content.cloneNode(true)] : []));
      nextMoment.hidden = this.items.length < 2;

      this.dialog.setAttribute('aria-label', data.creator ? `${data.creator}, moment ${data.number}` : `Moment ${data.number}`);
      this.setActive(0);
    }

    goTo(n, smooth = true) {
      if (!this.slides.length) return;
      const index = Math.max(0, Math.min(this.slides.length - 1, n));
      const slide = this.slides[index];
      const track = this.ui.track;
      const left = slide.offsetLeft - (track.clientWidth - slide.offsetWidth) / 2;
      const animate = smooth && !reducedMotion.matches;

      // Ignore intermediate positions while a programmatic smooth scroll runs.
      this.scrollLock = animate ? performance.now() + 700 : 0;
      track.scrollTo({ left, behavior: animate ? 'smooth' : 'auto' });
      this.setActive(index);
    }

    setActive(index) {
      this.index = index;
      const last = this.slides.length - 1;
      this.slides.forEach((slide, n) => slide.classList.toggle('is-active', n === index));
      Array.from(this.ui.bars.children).forEach((bar, n) => {
        bar.classList.toggle('is-active', n === index);
        bar.classList.toggle('is-seen', n < index);
        if (n === index) {
          bar.setAttribute('aria-current', 'true');
        } else {
          bar.removeAttribute('aria-current');
        }
      });
      this.ui.prev.setAttribute('aria-disabled', String(index === 0));
      this.ui.next.setAttribute('aria-disabled', String(index === last));
      if (last > 0) this.ui.status.textContent = `Photo ${index + 1} of ${last + 1}`;
    }

    onScroll() {
      if (this.scrollFrame) return;
      this.scrollFrame = requestAnimationFrame(() => {
        this.scrollFrame = 0;
        if (performance.now() < this.scrollLock || !this.slides.length) return;
        const track = this.ui.track;
        const center = track.scrollLeft + track.clientWidth / 2;
        let nearest = 0;
        let best = Infinity;
        this.slides.forEach((slide, n) => {
          const distance = Math.abs(slide.offsetLeft + slide.offsetWidth / 2 - center);
          if (distance < best) {
            best = distance;
            nearest = n;
          }
        });
        if (nearest !== this.index) this.setActive(nearest);
      });
    }

    onKeydown(event) {
      if (!this.dialog.open) return;
      if (event.key === 'ArrowRight') {
        event.preventDefault();
        this.goTo(this.index + 1);
      } else if (event.key === 'ArrowLeft') {
        event.preventDefault();
        this.goTo(this.index - 1);
      }
    }

    onDialogClick(event) {
      const target = event.target;
      const { bg, inner, track } = this.ui;
      if (target === this.dialog || target === bg || target === inner || target === track || target.classList.contains('dm-viewer__stage')) {
        this.close();
      }
    }

    animateIn(item) {
      const { bg, inner, chrome } = this.ui;
      const fadeIn = (el, duration, delay = 0) =>
        el.animate([{ opacity: 0 }, { opacity: 1 }], { duration, delay, easing: 'ease-out', fill: 'backwards' });

      const slide = this.slides[0];
      const cover = item.querySelector('.dm-card__cover');
      const coverImg = cover && cover.querySelector('img');
      const from = cover && cover.getBoundingClientRect();
      const to = slide && slide.getBoundingClientRect();

      fadeIn(bg, 450);
      if (reducedMotion.matches || !coverImg || !from.width || !to.width) {
        fadeIn(inner, 300);
        return;
      }

      chrome.forEach((el) => fadeIn(el, 500, 320));
      this.slides.slice(1).forEach((el, n) =>
        el.animate([{ opacity: 0, transform: 'translateX(80px) scale(0.9)' }], {
          duration: 800,
          delay: 260 + n * 90,
          easing: EASE,
          fill: 'backwards',
        })
      );

      const ghost = createGhost(coverImg, to);
      this.dialog.appendChild(ghost);
      slide.style.visibility = 'hidden';

      ghost
        .animate([{ transform: invert(from, to) }, { transform: 'none' }], { duration: 760, easing: EASE })
        .finished.then(async () => {
          slide.style.visibility = '';
          await imageReady(slide.querySelector('img'), 1500);
          if (!ghost.isConnected) return;
          await ghost.animate([{ opacity: 1 }, { opacity: 0 }], { duration: 280, easing: 'ease-out', fill: 'forwards' }).finished;
          ghost.remove();
        })
        .catch(() => ghost.remove());
    }

    close() {
      if (!this.dialog.open || this.closing) return;
      this.closing = true;
      this.dialog.querySelectorAll('.dm-ghost').forEach((ghost) => ghost.remove());

      const { bg, inner, chrome } = this.ui;
      const done = () => this.dialog.close();
      const fadeOut = (el, duration, delay = 0) =>
        el.animate([{ opacity: 1 }, { opacity: 0 }], { duration, delay, easing: 'ease-in', fill: 'forwards' }).finished;

      const item = this.items[this.moment];
      const cover = item && item.querySelector('.dm-card__cover');
      const slide = this.slides[this.index];
      const slideImg = slide && slide.querySelector('img');
      const coverImg = cover && cover.querySelector('img');
      const from = slide && slide.getBoundingClientRect();
      const to = cover && cover.getBoundingClientRect();
      const cardInView = to && to.width > 0 && to.bottom > 0 && to.top < window.innerHeight;

      if (reducedMotion.matches || !slide || !cardInView) {
        if (slide && !reducedMotion.matches) {
          slide.animate([{ opacity: 1, transform: 'none' }, { opacity: 0, transform: 'scale(0.94)' }], {
            duration: 320,
            easing: 'ease-in',
            fill: 'forwards',
          });
        }
        Promise.all([fadeOut(inner, 260), fadeOut(bg, 360)]).then(done, done);
        return;
      }

      chrome.forEach((el) => fadeOut(el, 200));
      this.slides.forEach((el) => {
        if (el !== slide) fadeOut(el, 200);
      });

      const ghost = createGhost(isLoaded(slideImg) ? slideImg : coverImg, to);
      this.dialog.appendChild(ghost);
      slide.style.visibility = 'hidden';

      // Morph back into the card; photos other than the cover dissolve as they land.
      const keyframes =
        this.index === 0
          ? [{ transform: invert(from, to) }, { transform: 'none' }]
          : [
              { transform: invert(from, to), opacity: 1 },
              { opacity: 1, offset: 0.55 },
              { transform: 'none', opacity: 0 },
            ];

      Promise.all([
        ghost.animate(keyframes, { duration: 620, easing: 'cubic-bezier(0.55, 0, 0.2, 1)', fill: 'forwards' }).finished,
        fadeOut(bg, 480, 100),
      ]).then(done, done);
    }

    async switchMoment(i) {
      if (this.switching || !this.dialog.open || i === this.moment) return;
      this.switching = true;

      const { track, meta, caption, shop, bars } = this.ui;
      const parts = [meta, caption, shop, bars];
      const out = { duration: 240, easing: 'ease-in', fill: 'forwards' };
      const leaving = [
        track.animate([{ opacity: 1, transform: 'none' }, { opacity: 0, transform: 'translateX(-56px)' }], out),
        ...parts.map((el) => el.animate([{ opacity: 1 }, { opacity: 0 }], out)),
      ];
      await Promise.all(leaving.map((animation) => animation.finished)).catch(() => {});

      if (this.dialog.open) {
        this.populate(i);
        this.goTo(0, false);
        this.updateHash(this.items[i].dataset.slug);
        // Keep the matching card on screen so closing can morph back into it.
        this.items[i].scrollIntoView({ block: 'center' });
        leaving.forEach((animation) => animation.cancel());
        track.animate([{ opacity: 0, transform: 'translateX(56px)' }, { opacity: 1, transform: 'none' }], {
          duration: 620,
          easing: EASE,
        });
        parts.forEach((el) => el.animate([{ opacity: 0 }, { opacity: 1 }], { duration: 420, delay: 120, easing: 'ease-out', fill: 'backwards' }));
      }

      this.switching = false;
    }

    cleanup() {
      this.closing = false;
      this.switching = false;
      document.removeEventListener('keydown', this.onKeydown);
      if (this.dialog) {
        this.dialog.getAnimations({ subtree: true }).forEach((animation) => animation.cancel());
        this.dialog.querySelectorAll('.dm-ghost').forEach((ghost) => ghost.remove());
      }
      if (this.ui) this.ui.track.replaceChildren();
      this.slides = [];
      this.unlockScroll();
    }

    lockScroll() {
      const root = document.documentElement;
      const scrollbar = window.innerWidth - root.clientWidth;
      root.style.overflow = 'hidden';
      if (scrollbar > 0) document.body.style.paddingRight = `${scrollbar}px`;
      this.scrollLocked = true;
    }

    unlockScroll() {
      if (!this.scrollLocked) return;
      document.documentElement.style.overflow = '';
      document.body.style.paddingRight = '';
      this.scrollLocked = false;
    }
  }

  customElements.define('dodona-moments', DodonaMoments);
})();
